import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialSharing = ({ plannerData, onShare }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  const socialPlatforms = [
    {
      name: 'Twitter',
      icon: 'Twitter',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      shareText: `Just created my personalized planner with StillFlow! 📅✨ #PlannerLife #Productivity`,
      action: (text, url) => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
      }
    },
    {
      name: 'Facebook',
      icon: 'Facebook',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      shareText: `Check out my planner design! Created with StillFlow.`,
      action: (text, url) => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank');
      }
    },
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      shareText: `Excited to share my new personalized planner created with StillFlow! Perfect for staying organized and productive.`,
      action: (text, url) => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, '_blank');
      }
    },
    {
      name: 'Pinterest',
      icon: 'Image',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      shareText: `Beautiful planner design - Created with StillFlow`,
      action: (text, url) => {
        window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}&media=${encodeURIComponent(plannerData?.thumbnailUrl)}`, '_blank');
      }
    }
  ];

  const handleShare = async (platform) => {
    setIsSharing(true);
    try {
      const shareUrl = `${window.location?.origin}/planner-preview?shared=${plannerData?.id}`;
      
      if (navigator.share && platform?.name === 'Native') {
        await navigator.share({
          title: 'My Planner',
          text: platform?.shareText,
          url: shareUrl
        });
      } else {
        platform?.action(platform?.shareText, shareUrl);
      }
      
      if (onShare) {
        onShare(platform?.name);
      }
      
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (error) {
      console.error('Share failed:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const shareUrl = `${window.location?.origin}/planner-preview?shared=${plannerData?.id}`;
      await navigator.clipboard?.writeText(shareUrl);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Share2" size={20} className="text-success" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground">
            Share Your Design
          </h2>
          <p className="font-body text-sm text-muted-foreground">
            Show off your planner
          </p>
        </div>
      </div>
      {/* Privacy Notice */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            <h4 className="font-body font-medium text-sm text-foreground mb-1">
              Privacy Protected
            </h4>
            <p className="font-caption text-xs text-muted-foreground">
              Only the planner design preview will be shared. Your personal information remains private.
            </p>
          </div>
        </div>
      </div>
      {/* Social Platforms */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {socialPlatforms?.map((platform) => (
          <button
            key={platform?.name}
            onClick={() => handleShare(platform)}
            disabled={isSharing}
            className={`
              p-4 rounded-lg border border-border hover:border-primary/20 
              transition-all duration-200 hover:scale-[1.02] group
              ${platform?.bgColor} hover:bg-primary/5 disabled:opacity-50
            `}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`${platform?.color} group-hover:text-primary transition-colors`}>
                <Icon name={platform?.icon} size={20} />
              </div>
              <span className="font-body text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                {platform?.name}
              </span>
            </div>
          </button>
        ))}
      </div>
      {/* Native Share & Copy Link */}
      <div className="space-y-3">
        {navigator.share && (
          <Button
            variant="outline"
            onClick={() => handleShare({ name: 'Native', shareText: 'Check out my planner design!' })}
            disabled={isSharing}
            loading={isSharing}
            iconName="Share"
            iconPosition="left"
            className="w-full"
          >
            Share via Device
          </Button>
        )}
        
        <Button
          variant="ghost"
          onClick={handleCopyLink}
          iconName={shareSuccess ? "Check" : "Copy"}
          iconPosition="left"
          className="w-full"
        >
          {shareSuccess ? 'Link Copied!' : 'Copy Share Link'}
        </Button>
      </div>
      {shareSuccess && (
        <div className="mt-4 flex items-center justify-center space-x-2 text-success text-sm">
          <Icon name="CheckCircle" size={14} />
          <span>Shared successfully!</span>
        </div>
      )}
    </div>
  );
};

export default SocialSharing;